import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useQueryClient } from '@tanstack/react-query';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import type { LegalAdviceFormOneData } from '../../../types/LegalAdvices/LegalAdviceFormOne.type';
import { fetchLegalAdviceById, saveLegalAdviceDraft, submitLegalAdviceForm } from '../../../services/LegalAdviceOneService';
import { DatalistInput } from '../components/dataListInput';
import { useSettings } from '../../../context/SettingContext';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';

const INITIAL_LEGAL_ADVICE_FORM_DATA: LegalAdviceFormOneData = {
  id: '',
  documentNumber: '',
  status: 'DRAFT',
  rejectionReason: '',
  submitDate: '',
  attachments: [],

  // Specific Form Details
  office: '',
  township: '',
  letterNo: '',
  date: '',
  refPoliceStation: '',
  refNo: '',
  complainant: '',
  accused: '',
  law: '',
  lawSection: '',
  policeOfficer: '',
  refLetterDate: '',
  refLetterNo: '',
  summary: '',
  witnessStatement: '',
  accusedStatement: '',
  seizedEvidence: '',
  medicalEvidence: '',
  analysis: '',
  requirements: '',
  legalAdvice: '',
  signName: '',
  signerName: '',
  lawOffice: '',
  policeoffice: '',
  copyTo:'',
  signatureImage: '',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<LegalAdviceFormOneData>;
}

export function LegalFormOne({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<LegalAdviceFormOneData>({
    ...INITIAL_LEGAL_ADVICE_FORM_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
   const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);
  const { settings } = useSettings();
  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];

  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null
  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchLegalAdviceById(id)
      .then((data: LegalAdviceFormOneData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const isFormSigned = Boolean(
    formData.signatureImage?.trim() && 
    (formData.signerName?.trim() || formData.signName?.trim())
  );

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['legalFormOne'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/legal-form-one');
    }
  };

  // Form Field Event Handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearDate = (name: keyof LegalAdviceFormOneData) => {
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, [name]: '' }));
  };

  const handleClearSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, signatureImage: '' }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };

  const handleSaveSignature = () => {
    if (!isEditable) return;
    if (sigCanvasRef.current) {
      if (sigCanvasRef.current.isEmpty()) {
        alert('လက်မှတ် အရင်ရေးထိုးပေးပါရန်။');
        return;
      }
      try {
        const canvas = sigCanvasRef.current.getTrimmedCanvas();
        const base64Data = canvas.toDataURL('image/png');
        setFormData((prev) => ({ ...prev, signatureImage: base64Data }));
        setIsSigning(false);
      } catch {
        const rawCanvas = sigCanvasRef.current.getCanvas();
        const base64Data = rawCanvas.toDataURL('image/png');
        setFormData((prev) => ({ ...prev, signatureImage: base64Data }));
        setIsSigning(false);
      }
    }
  };

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<LegalAdviceFormOneData> = { ...formData, status: 'DRAFT' };

      const response = await saveLegalAdviceDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.date?.trim()) {
      alert('ရက်စွဲ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    const nameOfSigner = (formData.signerName || formData.signName || '').trim();
    if (!formData.signatureImage?.trim() || !nameOfSigner) {
      alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      const payload: LegalAdviceFormOneData = {
        ...formData,
        id: targetId || '',
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'APPROVED',
      };

      const response = await saveLegalAdviceDraft(targetId || null, payload);
      const newId = response?.id || targetId;

      setFormData((prev) => ({
        ...prev,
        id: newId,
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'APPROVED',
      }));

      setIsApproved(true); // Locks form editing
      alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to approve and save:', err);
      alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (e: React.MouseEvent,
    targetLevels?: GeoLevel[] | null
  ) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.date?.trim()) {
      alert('ရက်စွဲ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

     // Use passed levels if available; otherwise use multi-select state
      const levelsToSubmit =
        targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

      // Validate that at least one checkbox level is selected
      if (!levelsToSubmit || levelsToSubmit.length === 0) {
        alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
        return;
      }


    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      if (!targetId) {
        const draftResponse = await saveLegalAdviceDraft(null, { ...formData, status: 'DRAFT' });
        targetId = draftResponse?.id;
        if (!targetId) {
          throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
        }

        setFormData((prev) => ({
          ...prev,
          ...draftResponse,
          id: targetId!,
        }));
      }

      const nameOfSigner = (formData.signerName || formData.signName || '').trim();
      const hasSigned = Boolean(formData.signatureImage?.trim() && nameOfSigner);
      // const nextStatus = hasSigned ? 'APPROVED' : 'SUBMITTED';

      if(!hasSigned){
        throw new Error('လက်မှတ်ထိုးပြီးမှသာ တင်ပြနိုင်ပါသည်');
      }

      const payload: LegalAdviceFormOneData = {
        ...formData,
        id: targetId,
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'SUBMITTED',
        submitDate: formData.date,
        targetGeoLevels: levelsToSubmit, // Array of selected levels
      };

      const response = await submitLegalAdviceForm(targetId, payload);

      setFormData((prev) => ({
        ...prev,
        id: targetId,
        status: response?.status || 'SUBMITTED',
      }));

      setIsApproved(true); // Locks form editing after submission
      alert(
        response?.status ==='SUBMITTED'
          ?  'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)' : 'တင်ပြမှုမအောင်မြင်ပါ'
      );
      //navigate('/legal-form-one');
    } catch (err: any) {
      console.error('Failed to submit form:', err);
      alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDatePicker = (
    name: Extract<keyof LegalAdviceFormOneData, string>
  ) => (
    <FormDatePicker
      name={name}
      value={(formData?.[name] as string) || ''}
      isEditable={isEditable}
      onChange={handleChange}
      onClear={handleClearDate}
    />
  );

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
    const status = formData.status || 'DRAFT';

    // Extract and format Geo Level text
    const rawGeo = formData.targetGeoLevels || formData.geoLevel;
    const formatGeo = (geo: any) => {
      if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
      if (Array.isArray(geo)) {
        return geo
          .map((g) => GEO_LEVEL_MAP[g] || g)
          .filter(Boolean)
          .join(' | ');
      }
      return '';
    };
    const geoText = formatGeo(rawGeo);

    const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
  > = {
    DRAFT: {
      label: 'မူကြမ်း',
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    APPROVED: {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-600',
    },
    SUBMITTED: {
      label: 'တင်ပြပြီး',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    UNDER_REVIEW: {
      label: 'ကြည့်ရှုထားသည်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
    ATTACHED: {
      label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
  };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

  return (
    <PageLayoutWrapper
      handleBackNavigation={handleBackNavigation}
      renderStageProgression={renderStageProgression}
      isEditable={isEditable}
      isPreview={isPreview}
      readOnlyMessage={
        formData.status === 'APPROVED'
          ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
          : formData.status === 'SUBMITTED'
            ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : undefined
      }
      setIsPreview={setIsPreview}
    >
      {/* A4 Sheet Container */}
      <div className="w-full max-w-[800px] bg-white shadow-lg border border-gray-300 p-12 text-black leading-relaxed relative text-sm md:text-base print:p-0 print:border-none print:shadow-none">
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs text-gray-400 print:top-0 print:left-1/2">
          လျှို့ဝှက်
        </div>
        <div className="absolute top-4 right-4 text-xs font-bold print:top-0 print:right-0">
          ဥပရအမှုလိုက်ပုံစံ (၁)
        </div>

        {/* Header Section */}
        <div className="text-center space-y-2 mt-4">
          <h1 className="text-lg font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>

          {/* ရုံး */}
          <div className="flex justify-center items-center gap-2">
            <DatalistInput
              name="office"
              value={formData.office || ""}
              onChange={handleChange}
              placeholder=""
              options={OFFICE_OPTIONS}
              disabled={!isEditable}
            />
            <span className="w-24 text-left">ရုံး</span>
          </div>

          {/* မြို့နယ် */}
          <div className="flex justify-center items-center gap-2">
            <DatalistInput
              name="township"
              value={formData.township || ""}
              onChange={handleChange}
              placeholder=""
              options={TOWNSHIP_OPTIONS}
              disabled={!isEditable}
            />
            <span className="w-24 text-left">မြို့နယ်</span>
          </div>
        </div>

        {/* Letter No & Date Meta Section */}
        <div className="flex flex-col items-end space-y-2 mt-6">
          <div className="flex items-center gap-1 w-64 justify-end">
            <span className="whitespace-nowrap">စာအမှတ်၊</span>
            <input
              type="text"
              name="letterNo"
              value={formData.letterNo || ""}
              onChange={handleChange}
              disabled={!isEditable}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
            />
          </div>

          <div className="flex items-center gap-2 w-64 justify-end">
            <span className="whitespace-nowrap">ရက်စွဲ၊</span>
            <div className="flex-grow">{renderDatePicker('date')}</div>
          </div>
        </div>

        {/* Content Section */}
        <div className="mt-8 space-y-6">
          <div className="flex items-start gap-2">
            <span className="font-bold whitespace-nowrap">အကြောင်းအရာ။</span>
            <p className="font-bold underline-offset-4">
              ပြစ်မှုဆိုင်ရာအမှုများကို တရားစွဲဆိုခြင်းမပြုမီ ဥပဒေအကြံပေးခြင်း
            </p>
          </div>

          {/* ရည်ညွှန်းချက် */}
          <div className="space-y-3">
            <span className="font-bold block">ရည်ညွှန်းချက်။</span>

            {/* ရည်ညွှန်းချက် (၁) */}
            <div className="pl-6 text-justify leading-8">
              (၁){" "}
              {/* <DatalistInput
                name="refPoliceStation"
                value={formData.refPoliceStation || ""}
                onChange={handleChange}
                placeholder=""
                options={OFFICE_OPTIONS}
                disabled={!isEditable}
                className="w-36 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
              /> */}
              <input
                type="text"
                name="refPoliceStation"
                value={formData.refPoliceStation || ""}
                onChange={handleChange}
                disabled={!isEditable}
                className="w-24 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
              />
              ရဲစခန်း၊ (ပ) အမှတ်{" "}
              <input
                type="text"
                name="refNo"
                value={formData.refNo || ""}
                onChange={handleChange}
                disabled={!isEditable}
                className="w-24 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
              />
              တရားလို{" "}
              <input
                type="text"
                name="complainant"
                value={formData.complainant || ""}
                onChange={handleChange}
                disabled={!isEditable}
                className="w-32 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
              />
              နှင့် တရားခံ{" "}
              <input
                type="text"
                name="accused"
                value={formData.accused || ""}
                onChange={handleChange}
                disabled={!isEditable}
                className="w-32 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
              />

              ၊
              <input
                type="text"
                name="law"
                value={formData.law || ""}
                onChange={handleChange}
                disabled={!isEditable}
                className="w-32 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
              />
              ဥပဒေ
               ၊ပုဒ်မ{" "}
              <input
                type="text"
                name="lawSection"
                value={formData.lawSection || ""}
                onChange={handleChange}
                disabled={!isEditable}
                className="w-28 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
              />
              အရေးယူထားသော ရဲစက္ကူအမှုတွဲ။
            </div>

            {/* ရည်ညွှန်းချက် (၂) */}
            <div className="pl-6 text-justify leading-8">
              (၂){" "}
              <DatalistInput
                name="policeoffice"
                value={formData.policeoffice || ""}
                onChange={handleChange}
                placeholder=""
                options={OFFICE_OPTIONS}
                disabled={!isEditable}
                className="w-48 bg-transparent border-b border-black focus:outline-none py-0.5 text-center mx-1"
              />
              ရဲတပ်ဖွဲ့မှူးရုံး၏{" "}
              <div className="flex-grow">{renderDatePicker('refLetterDate')}</div>
              ရက်စွဲပါစာအမှတ်{" "}
              <input
                type="text"
                name="refLetterNo"
                value={formData.refLetterNo || ""}
                onChange={handleChange}
                disabled={!isEditable}
                className="w-32 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
              />
            </div>
          </div>

          <hr className="border-black my-4" />

          {/* Form Content Body */}
          <div className="space-y-6">
            <div>
              <span className="font-bold">၁။ အမှုအကြောင်းအရာ အကျဉ်းချုပ်</span>
              <textarea
                name="summary"
                value={formData.summary || ""}
                onChange={handleChange}
                disabled={!isEditable}
                rows={3}
                className="w-full mt-2 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
              />
            </div>

            <div>
              <span className="font-bold">၂။ လေ့လာတွေ့ရှိချက်များ</span>
              <div className="pl-6 mt-2 space-y-4">
                <div>
                  <span>(က) တရားလိုနှင့် သက်သေထွက်ဆိုချက်များ အကျဉ်း</span>
                  <textarea
                    name="witnessStatement"
                    value={formData.witnessStatement || ""}
                    onChange={handleChange}
                    disabled={!isEditable}
                    rows={2}
                    className="w-full mt-1 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
                  />
                </div>
                <div>
                  <span>(ခ) တရားခံထွက်ဆိုချက်အကျဉ်း</span>
                  <textarea
                    name="accusedStatement"
                    value={formData.accusedStatement || ""}
                    onChange={handleChange}
                    disabled={!isEditable}
                    rows={2}
                    className="w-full mt-1 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
                  />
                </div>
                <div>
                  <span>(ဂ) သိမ်းဆည်းရမိသော သက်သေခံပစ္စည်းများနှင့် စာရွက်စာတမ်းများ</span>
                  <textarea
                    name="seizedEvidence"
                    value={formData.seizedEvidence || ""}
                    onChange={handleChange}
                    disabled={!isEditable}
                    rows={2}
                    className="w-full mt-1 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
                  />
                </div>
                <div>
                  <span>
                    (ဃ) ဆေးစာ၊ ကျွမ်းကျင်သူ ထင်မြင်ချက်၊ ဖြောင့်ချက်၊ ကြိုတင်ထွက်ဆိုချက်နှင့်
                    အခြားစာရွက်စာတမ်းများ
                  </span>
                  <textarea
                    name="medicalEvidence"
                    value={formData.medicalEvidence || ""}
                    onChange={handleChange}
                    disabled={!isEditable}
                    rows={2}
                    className="w-full mt-1 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
                  />
                </div>
              </div>
            </div>

            <div>
              <span className="font-bold">၃။ သုံးသပ်ချက်</span>
              <textarea
                name="analysis"
                value={formData.analysis || ""}
                onChange={handleChange}
                disabled={!isEditable}
                rows={3}
                className="w-full mt-2 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
              />
            </div>

            <div>
              <span className="font-bold">
                ၄။ အမှုစစ် ရဲအရာရှိက ထပ်မံစစ်ဆေးဆောင်ရွက်ရန် လိုအပ်ချက်များ (ရှိလျှင်)
              </span>
              <textarea
                name="requirements"
                value={formData.requirements || ""}
                onChange={handleChange}
                disabled={!isEditable}
                rows={2}
                className="w-full mt-2 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
              />
            </div>

            <div>
              <span className="font-bold">၅။ ဥပဒေအကြံပြုချက်</span>
              <textarea
                name="legalAdvice"
                value={formData.legalAdvice || ""}
                onChange={handleChange}
                disabled={!isEditable}
                rows={3}
                className="w-full mt-2 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
              />
            </div>
          </div>

          {/* Footer Guidelines Block */}
          <div className="text-xs space-y-1 mt-8 pt-4 border-t border-gray-200 text-gray-700 print:text-black">
            <p>
              ၆။ ရည်ညွှန်းချက် (၂) ပါစာဖြင့် ပူးတွဲပေးပို့ထားသော ရည်ညွှန်းချက် (၁) ပါ ရဲစက္ကူ
              အမှုတွဲကို ပူးတွဲ၍ ပြန်လည်ပေးပို့ပါသည်။
            </p>
            <p>၇။ ဤအကြံပြုချက်သည် တရားစွဲအဖွဲ့အစည်းသို့သာ ဥပဒေအရ ပြန်ကြားခြင်းဖြစ်ပါသည်။</p>
            <p>
              ၈။ အထက်အပိုဒ် ၅ ပါ ဥပဒေအကြံပြုချက်အတိုင်း လိုက်နာဆောင်ရွက်ခြင်း ရှိမရှိ
              ပြန်ကြားပေးရန်ဖြစ်ပါသည်။
            </p>
            <p>၉။ ဤအကြံပြုချက်ကို လျှို့ဝှက်အဆင့်သတ်မှတ်ဆောင်ရွက်ရန်ဖြစ်ပါသည်။</p>
          </div>

          {/* Signatures Panel Container */}
          <div className="mt-12 pr-6 flex flex-col items-end space-y-4">
            <div className="w-64 flex flex-col space-y-3">
              {/* လက်မှတ် */}
              <div className="flex items-center">
                <span className="w-24 text-right text-sm whitespace-nowrap mr-2">လက်မှတ်</span>
                <div
                  onClick={() => isEditable && setIsSigning(true)}
                  className={`flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center relative group ${
                    isEditable ? "cursor-pointer hover:bg-gray-50 transition" : ""
                  }`}
                >
                  {formData.signatureImage ? (
                    <>
                      <img
                        src={formData.signatureImage}
                        alt="Signature"
                        className="h-8 object-contain mix-blend-multiply"
                      />
                      {isEditable && (
                        <span className="absolute inset-0 bg-black/40 text-white text-xs opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded print:hidden">
                          လက်မှတ်အသစ်ထိုးရန် နှိပ်ပါ
                        </span>
                      )}
                    </>
                  ) : (
                    isEditable && (
                      <span className="text-gray-400 text-xs font-sans print:hidden">
                        ✍️ ဒီနေရာတွင် လက်မှတ်ထိုးရန် နှိပ်ပါ
                      </span>
                    )
                  )}
                </div>
                {formData.signatureImage && (
                    <button
                      type="button"
                      onClick={handleClearSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
              </div>

              {/* အမည် */}
              <div className="flex items-center">
                <span className="w-24 text-right text-sm whitespace-nowrap mr-2">အမည်</span>
                <input
                  type="text"
                  name="signName"
                  value={formData.signName || ""}
                  onChange={handleChange}
                  disabled={!isEditable}
                  className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
                />
              </div>

              <div className="text-center text-xs font-bold py-1">တာဝန်ခံဥပဒေအရာရှိ</div>

              {/* ဥပဒေရုံး */}
              <div className="flex items-center">
                <DatalistInput
                  name="lawOffice"
                  value={formData.lawOffice || ""}
                  onChange={handleChange}
                  placeholder=""
                  options={OFFICE_OPTIONS}
                  disabled={!isEditable}
                  className="flex-1 bg-transparent border-b border-black focus:outline-none py-0.5 text-center"
                />
                <span className="w-24 text-left text-sm ml-2 whitespace-nowrap">ဥပဒေရုံး</span>
              </div>
            </div>

            <div className="w-64 self-start flex flex-col space-y-3">
              {/* ရဲတပ်ဖွဲ့မှူး */}
              <div className="flex items-center">
                <input
                  type="text"
                  name="policeOfficer"
                  value={formData.policeOfficer || ""}
                  onChange={handleChange}
                  disabled={!isEditable}
                  className="flex-1 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
                />
                <span className="w-24 text-left text-sm ml-2 whitespace-nowrap">ရဲတပ်ဖွဲ့မှူး</span>
              </div>

              {/* ရဲတပ်ဖွဲ့မှူးရုံး */}
              <div className="flex items-center">
                {/* <DatalistInput
                  name="policeoffice"
                  value={formData.policeoffice || ""}
                  onChange={handleChange}
                  placeholder=""
                  options={OFFICE_OPTIONS}
                  disabled={!isEditable}
                  className="flex-1 bg-transparent border-b border-black focus:outline-none py-0.5 text-center"
                /> */}
                <input
                  type="text"
                  name="policeoffice"
                  value={formData.policeoffice || ""}
                  onChange={handleChange}
                  disabled={!isEditable}
                  className="flex-1 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
                />
                <span className="w-24 text-left text-sm ml-2 whitespace-nowrap">ရဲတပ်ဖွဲ့မှူးရုံး</span>
              </div>

              {/* မြို့ */}
              <div className="flex items-center">
                <DatalistInput
                  name="township"
                  value={formData.township || ""}
                  onChange={handleChange}
                  placeholder=""
                  options={TOWNSHIP_OPTIONS}
                  disabled={!isEditable}
                  className="flex-1 text-center bg-transparent border-b border-black focus:outline-none py-0.5"
                />
                <span className="w-24 text-left text-sm ml-2 whitespace-nowrap">မြို့</span>
              </div>
            </div>

            {/* CC Section */}
            <div className="w-full mt-4 self-stretch text-left">
              <span className="text-sm font-medium block mb-1">မိတ္တူကို-</span>
              <textarea
                name="copyTo"
                value={formData.copyTo || ""}
                onChange={handleChange}
                disabled={!isEditable}
                rows={2}
                className="block w-full max-w-md mx-auto p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-center text-sm print:border-none print:p-0"
              />
            </div>
          </div>
        </div>
        {/* Action Buttons */}
        {/* Form Action Buttons Component Integration */}
          <FormActionButtons
            isPreview={isPreview}
            isEditable={isEditable}
            isSubmitting={isSubmitting}
            isFormSigned={isFormSigned}
            isApproved={isApproved}
            targetGeoLevels={formData.targetGeoLevels}
            currentGeoLevel={currentUser?.scopeLevel}
            selectedGeoLevels={selectedGeoLevels}
            onGeoLevelChange={setSelectedGeoLevels}
            onCancel={handleBackNavigation}
            onSaveDraft={handleSaveDraft}
            onApproveAndSave={handleApproveAndSave}
            onSubmitToSuperior={handleSubmitToSuperior}
          />

        {/* Attachment Section */}
        {/* <AttachmentSection
          attachments={formData.attachments}
          isEditable={isEditable}
          isPreview={isPreview}
          onFileChange={handleFileChange}
          onRemoveFile={handleRemoveFile}
        /> */}

        {/* Signature Modal */}
        <SignatureModal
          isOpen={isSigning}
          sigCanvasRef={sigCanvasRef}
          onClose={() => setIsSigning(false)}
          onClear={() => sigCanvasRef.current?.clear()}
          onSave={handleSaveSignature}
        />

        <div className="absolute bottom-4 left-4 text-xs text-gray-400 print:bottom-0 print:left-0">
          လျှို့ဝှက်
        </div>
      
      </div>
    </PageLayoutWrapper>
  );
}